(() => {
  'use strict';

  let productDataPromise;

  const getProductHandle = () => {
    const picker = document.querySelector('variant-picker[data-product-handle]');
    return picker?.dataset.productHandle || window.location.pathname.split('/products/')[1]?.split('/')[0];
  };

  const getPicker = () => document.querySelector('variant-picker');

  const getSelectedVariantId = () => {
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant) return String(urlVariant);

    const picker = getPicker();
    if (!picker) return '';
    const selectedOption = picker.querySelector('select option:checked, select option[selected]');
    if (selectedOption?.dataset.variantId) return selectedOption.dataset.variantId;
    return picker.querySelector('input[type="radio"]:checked')?.dataset.variantId || '';
  };

  const getSelectedOptions = () => {
    const picker = getPicker();
    if (!picker) return [];
    const selects = [...picker.querySelectorAll('select')];
    if (selects.length) return selects.map((select) => select.value || '').filter(Boolean);
    return [...picker.querySelectorAll('input[type="radio"]:checked')]
      .map((input) => input.value || '').filter(Boolean);
  };

  const loadProductData = async () => {
    const handle = getProductHandle();
    if (!handle) return null;
    if (!productDataPromise) {
      productDataPromise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      }).then((response) => (response.ok ? response.json() : null)).catch(() => null);
    }
    return productDataPromise;
  };

  const getSelectedVariant = async () => {
    const product = await loadProductData();
    if (!product?.variants?.length) return null;
    const variantId = getSelectedVariantId();
    const byId = product.variants.find((variant) => String(variant.id) === String(variantId));
    if (byId) return byId;
    const options = getSelectedOptions();
    return product.variants.find((variant) =>
      options.every((option, index) => String(variant.options?.[index] || '') === String(option))
    ) || null;
  };

  const getGallery = () => document.querySelector('media-gallery');

  const getGalleryImages = () => {
    const gallery = getGallery();
    if (!gallery) return [];
    return [...gallery.querySelectorAll('img')].map((img) => ({
      img,
      src: img.currentSrc || img.src || '',
      alt: img.alt || '',
    })).filter((item) => item.src);
  };

  const normalize = (value) => String(value || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and').replace(/[^a-z0-9]/g, '');

  const findByMediaId = (mediaId) => {
    const gallery = getGallery();
    if (!gallery || !mediaId) return null;
    const media = [...gallery.querySelectorAll('[data-media-id]')]
      .find((element) => String(element.dataset.mediaId) === String(mediaId));
    const img = media?.querySelector('img');
    return img ? { img } : null;
  };

  const findByImageUrl = (src) => {
    if (!src) return null;
    const target = normalize(src.split('?')[0]);
    return getGalleryImages().find((item) => normalize(item.src.split('?')[0]) === target) || null;
  };

  const findByOptions = (options) => {
    const tokens = options.flatMap((option) => normalize(option).match(/[a-z0-9]{4,}/g) || []);
    let best = null;
    let bestScore = 0;
    for (const item of getGalleryImages()) {
      const searchable = normalize(`${item.src} ${item.alt}`);
      const score = tokens.reduce((total, token) => total + (searchable.includes(token) ? 1 : 0), 0);
      if (score > bestScore) { bestScore = score; best = item; }
    }
    return bestScore > 0 ? best : null;
  };

  const selectGalleryImage = (matchedImage) => {
    if (!matchedImage) return;
    const gallery = getGallery();
    const slide = matchedImage.img.closest('slideshow-slide');
    const slideshow = gallery?.querySelector('slideshow-component, slideshow-slides');
    if (slide && slideshow?.select) {
      const slides = slideshow.slides ? [...slideshow.slides] : [...slideshow.querySelectorAll('slideshow-slide')];
      const index = slides.indexOf(slide);
      if (index >= 0) slideshow.select(index, undefined, { animate: false });
    } else {
      matchedImage.img.closest('.product-media-container')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const updateVariantUrl = () => {
    const variantId = getSelectedVariantId();
    if (!variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  };

  const run = async () => {
    const variant = await getSelectedVariant();
    let matched = variant?.featured_image?.id ? findByMediaId(variant.featured_image.id) : null;
    if (!matched && variant?.featured_image?.src) matched = findByImageUrl(variant.featured_image.src);
    if (!matched) matched = findByOptions(getSelectedOptions());
    selectGalleryImage(matched);
    updateVariantUrl();
  };

  const scheduleRun = () => [250, 700, 1400].forEach((delay) => setTimeout(run, delay));
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('variant-picker')) scheduleRun();
  });
  document.addEventListener('DOMContentLoaded', () => setTimeout(run, 500));
})();
